import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon } from '@heroicons/react/24/solid';
import AuthService from '../../../service/AuthService';
import ContactService from '../../../service/ContactService';
import { addAuthInterceptor } from '../../../service/AuthInterceptor';
import Banner from '../../kit/Banner';
import context from '../../../exports.json';

const INTRODUCTION_STRUCTURE = [
    "introduction_structure_point_1",
    "introduction_structure_point_2",
    "introduction_structure_point_3",
    "introduction_structure_point_4",
];

/**
 * Introduction builder section with AI generation.
 * @param {object} props
 * @param {object} props.contact - The contact object containing the intro.
 * @param {function} props.onUpdate - Callback to send data back to parent.
 */
export default function Introduction({ contact: initialContact, onUpdate }) {
    const { t } = useTranslation();
    const [introductionParagraph, setIntroductionParagraph] = useState(initialContact?.introductionParagraph || '');
    const [isGenerating, setIsGenerating] = useState(false);

    // Memoize services so they aren't re-created on every render
    const contactService = useMemo(() => {
        const authService = new AuthService(context.ApiGatewayUrl);
        return new ContactService(
            context.ApiGatewayUrl,
            addAuthInterceptor(authService)
        );
    }, []);

    // Sync state if the prop changes (Edit mode)
    useEffect(() => {
        if (initialContact?.introductionParagraph !== undefined) {
            setIntroductionParagraph(initialContact.introductionParagraph || '');
        }
    }, [initialContact]);

    const handleAutoGenerate = async () => {
        setIsGenerating(true);
        try {
            // Passing current contact context to the service if needed for better AI generation
            const response = await contactService.createParagraph(initialContact);
            updateIntro(response);
        } catch (error) {
            console.error('Failed to generate intro:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const updateIntro = (text) => {
        setIntroductionParagraph(text);
        if (onUpdate) {
            onUpdate({ ...initialContact, introductionParagraph: text });
        }
    };

    return (
        <div className="space-y-6">
            {!initialContact?.id && (
                <Banner
                    variant="info"
                    title={t('introduction_banner_title')}
                    message={t('introduction_banner_description')}
                />
            )}

            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                        {t('introduction_builder_title')}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('introduction_builder_description')}
                    </p>
                </div>

                <button
                    id="auto-generate-intro"
                    onClick={handleAutoGenerate}
                    disabled={isGenerating || !initialContact?.id}
                    className="
                        flex items-center space-x-2 px-3 py-2 
                        text-sm font-medium rounded-lg transition-colors 
                        bg-white border border-gray-300 shadow-sm
                        text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                        disabled:opacity-70 disabled:cursor-not-allowed
                    "
                >
                    {isGenerating ? (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : <SparklesIcon className="h-5 w-5 text-gray-600" />}
                    <span>{t('introduction_auto_generate_button')}</span>
                </button>
            </div>

            <div>
                <h3 className="text-base font-semibold text-gray-700 mb-2">
                    {t('introduction_structure_title')}
                </h3>
                <ol className="space-y-2 text-gray-600 text-sm list-decimal ml-5">
                    {INTRODUCTION_STRUCTURE.map((key, index) => (
                        <li key={index} className="pl-1">{t(key)}</li>
                    ))}
                </ol>
            </div>

            <div>
                <label htmlFor="introductionParagraph" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('introduction_paragraph_label')}
                </label>
                <textarea
                    id="introductionParagraph"
                    value={introductionParagraph}
                    onChange={(e) => updateIntro(e.target.value)}
                    placeholder={t('introduction_paragraph_placeholder')}
                    rows={5}
                    className="
                        block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                        placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                        sm:text-sm resize-y
                    "
                />
                <p className="mt-2 text-xs text-gray-500">
                    {t('introduction_paragraph_tip')}
                </p>
            </div>
        </div>
    );
}